/**
 * Utilitários gerais do sistema
 */

import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DATE_MIDNIGHT_RE =
  /^(\d{4})-(\d{2})-(\d{2})[T ]00:00:00(?:\.\d+)?(?:Z|[+-]00:00)?$/;

function pad2(n) {
  return String(n).padStart(2, "0");
}

/**
 * Interpreta data de calendário (nascimento, matrícula, frequência) no fuso local.
 * Strings `YYYY-MM-DD` ou meia-noite UTC não devem virar o dia anterior no Brasil.
 */
export const parseLocalDate = value => {
  if (value === undefined || value === null || value === "") return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const str = String(value).trim();
  const dateOnly = str.match(DATE_ONLY_RE) || str.match(DATE_MIDNIGHT_RE);
  if (dateOnly) {
    const local = new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
    return Number.isNaN(local.getTime()) ? null : local;
  }
  const parsed = new Date(str);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * YYYY-MM-DD no calendário local (inputs type="date" e APIs de data).
 */
export const toDateInputValue = value => {
  const d = parseLocalDate(value);
  if (!d) return "";
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

/**
 * Formata uma data para exibição (DD/MM/YYYY por padrão), sem deslocar o dia.
 */
export const formatDate = (date, format = "dd/MM/yyyy") => {
  if (!date) return "";

  const d = parseLocalDate(date);
  if (!d) return "";

  const day = pad2(d.getDate());
  const month = pad2(d.getMonth() + 1);
  const year = String(d.getFullYear());

  return format.replace("dd", day).replace("MM", month).replace("yyyy", year);
};

/**
 * Formata CPF com máscara progressiva (aplica enquanto digita)
 */
export const formatCPF = cpf => {
  if (!cpf) return "";
  const cleaned = cpf.replace(/\D/g, "");
  
  // Aplica máscara progressivamente conforme o usuário digita
  if (cleaned.length <= 3) {
    return cleaned;
  } else if (cleaned.length <= 6) {
    return cleaned.replace(/(\d{3})(\d+)/, "$1.$2");
  } else if (cleaned.length <= 9) {
    return cleaned.replace(/(\d{3})(\d{3})(\d+)/, "$1.$2.$3");
  } else {
    return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d+)/, "$1.$2.$3-$4");
  }
};

/**
 * Formata telefone
 */
export const formatPhone = phone => {
  if (!phone) return "";
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.length === 11) {
    return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
  }
  if (cleaned.length === 10) {
    return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, "($1) $2-$3");
  }
  return phone;
};

/**
 * Valida email
 */
export const isValidEmail = email => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Valida força da senha (espelha UserAdminCreate no backend).
 * Mínimo 8 caracteres, com maiúscula, minúscula, número e caractere especial.
 * @returns {{ valid: boolean, message: string|null }}
 */
export const validatePasswordStrength = password => {
  if (!password) {
    return { valid: false, message: "Senha é obrigatória" };
  }

  if (password.length < 8) {
    return { valid: false, message: "Senha deve ter no mínimo 8 caracteres" };
  }

  const hasUpper = /[A-Z]/.test(password);
  const hasLower = /[a-z]/.test(password);
  const hasDigit = /[0-9]/.test(password);
  const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);

  if (!(hasUpper && hasLower && hasDigit && hasSpecial)) {
    return {
      valid: false,
      message:
        "Senha deve conter: maiúscula, minúscula, número e caractere especial (!@#$%^&* etc)",
    };
  }

  return { valid: true, message: null };
};

/**
 * Valida CPF
 */
export const isValidCPF = cpf => {
  const cleaned = cpf.replace(/\D/g, "");
  if (cleaned.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(cleaned)) return false;

  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cleaned.charAt(i)) * (10 - i);
  }
  let digit = 11 - (sum % 11);
  if (digit >= 10) digit = 0;
  if (digit !== parseInt(cleaned.charAt(9))) return false;

  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cleaned.charAt(i)) * (11 - i);
  }
  digit = 11 - (sum % 11);
  if (digit >= 10) digit = 0;
  if (digit !== parseInt(cleaned.charAt(10))) return false;

  return true;
};

/**
 * Debounce function
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Classe para condicionais (com clsx e tailwind-merge)
 */
export const cn = (...inputs) => {
  return twMerge(clsx(inputs));
};

/**
 * Função reutilizável para imprimir uma imagem
 * @param {string} imageUrl - URL da imagem a ser impressa
 * @param {string} title - Título do documento (opcional)
 */
export const printImage = (imageUrl, title = "Documento") => {
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    console.error('Não foi possível abrir a janela de impressão. Verifique se os pop-ups estão bloqueados.');
    return;
  }

  // Obtém a URL base da aplicação se a imagem for relativa
  const baseUrl = window.location.origin;
  const fullImageUrl = imageUrl.startsWith('http') ? imageUrl : `${baseUrl}${imageUrl.startsWith('/') ? '' : '/'}${imageUrl}`;

  const printHTML = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8">
        <title>${title}</title>
        <style>
          @page {
            size: A4 portrait;
            margin: 0;
          }
          
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          html, body {
            width: 100%;
            height: 100%;
            overflow: hidden;
          }
          
          body {
            display: flex;
            align-items: center;
            justify-content: center;
            background: white;
          }
          
          img {
            max-width: 100%;
            max-height: 100vh;
            width: auto;
            height: auto;
            object-fit: contain;
          }
          
          @media print {
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
            
            html, body {
              width: 100%;
              height: 100%;
              overflow: visible;
            }
            
            body {
              display: flex;
              align-items: center;
              justify-content: center;
            }
            
            img {
              max-width: 100%;
              max-height: 100vh;
              width: auto;
              height: auto;
              object-fit: contain;
            }
          }
        </style>
      </head>
      <body>
        <img src="${fullImageUrl}" alt="${title}" onload="window.print()" />
      </body>
    </html>
  `;

  printWindow.document.write(printHTML);
  printWindow.document.close();
  
  // Fallback caso o onload não funcione
  printWindow.onload = () => {
    setTimeout(() => {
      printWindow.print();
      printWindow.onafterprint = () => {
        printWindow.close();
      };
    }, 250);
  };
};

export default {
  formatDate,
  formatCPF,
  formatPhone,
  isValidEmail,
  isValidCPF,
  validatePasswordStrength,
  debounce,
  cn,
  printImage,
};
